import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Button, Badge, Input } from '../../components/ui';
import { Save, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { programsAPI, technologiesAPI, type Program, type Technology } from '../../lib/services';

const ProgramSettings = () => {
  const { programId } = useParams<{ programId: string }>();
  const [program, setProgram] = useState<Program | null>(null);
  const [technologies, setTechnologies] = useState<Technology[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Form state
  const [name, setName] = useState<string>('');
  const [technologyId, setTechnologyId] = useState<number | null>(null);
  const [status, setStatus] = useState<string>('');

  useEffect(() => {
    if (!programId) return;
    
    const fetchData = async () => {
      try {
        setLoading(true);
        const [programData, techData] = await Promise.all([
          programsAPI.get(parseInt(programId)),
          technologiesAPI.list()
        ]);
        
        setProgram(programData);
        setName(programData.name);
        setTechnologyId(programData.technology);
        setStatus(programData.status);
        setTechnologies(techData);
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError('Failed to load program settings');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [programId]);

  const handleSave = async () => {
    if (!programId || !program) return;

    // Validation
    if (!name.trim()) {
      setError('Program name is required');
      return;
    }

    if (!technologyId) {
      setError('Technology is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      await programsAPI.update(parseInt(programId), {
        name: name.trim(),
        technology: technologyId,
        status: status as any,
      });

      setSuccessMessage('Program settings updated successfully');
      
      // Refresh program data
      const updated = await programsAPI.get(parseInt(programId));
      setProgram(updated);
      setName(updated.name);
      setTechnologyId(updated.technology);
      setStatus(updated.status);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err: any) {
      console.error('Failed to update program:', err);
      setError(err.message || 'Failed to update program settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!program) return;
    setName(program.name);
    setTechnologyId(program.technology);
    setStatus(program.status);
    setError(null);
  };

  const hasChanges = program && (
    name !== program.name ||
    technologyId !== program.technology ||
    status !== program.status
  );

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'approved' as const;
      case 'draft':
        return 'pending' as const;
      case 'review':
        return 'in-progress' as const;
      case 'archived':
        return 'rejected' as const;
      default:
        return 'pending' as const;
    }
  };

  const getStatusDescription = (status: string) => {
    switch (status) {
      case 'draft':
        return 'Program is in draft mode. Not visible to vendors.';
      case 'review':
        return 'Program is under review. Not visible to vendors.';
      case 'active':
        return 'Program is active. Vendors can view and submit to this program.';
      case 'archived':
        return 'Program is archived. No new submissions accepted.';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (!program) {
    return (
      <div className="p-8">
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
          <p className="text-red-900 dark:text-red-100">Program not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-background">
      <div className="max-w-4xl mx-auto px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-foreground">Program Settings</h1>
          <p className="text-base text-muted-foreground mt-2">
            Configure program settings and visibility
          </p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-success/10 border border-success/20 rounded-lg p-4 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
            <p className="text-sm text-success-foreground">{successMessage}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Basic Information Card */}
        <Card className="p-6 mb-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Basic Information
            </h2>
            <p className="text-sm text-muted-foreground">
              Edit program name and technology
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Program Name <span className="text-destructive">*</span>
              </label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter program name"
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Technology <span className="text-destructive">*</span>
              </label>
              <select
                value={technologyId || ''}
                onChange={(e) => setTechnologyId(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-4 py-3 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent shadow-xs"
              >
                <option value="">Select a technology...</option>
                {technologies.map((tech) => (
                  <option key={tech.id} value={tech.id}>
                    {tech.full_path || tech.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-muted-foreground">
                Technology determines which scorecard templates will be used
              </p>
            </div>
          </div>
        </Card>

        {/* Program Status Card */}
        <Card className="p-6 mb-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Program Status
            </h2>
            <p className="text-sm text-muted-foreground">
              Control when vendors can see and submit to this program
            </p>
          </div>

          {/* Current Status Display */}
          <div className="mb-6 p-4 bg-background rounded-lg border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-card-foreground">
                Current Status
              </span>
              <Badge variant={getStatusBadgeVariant(program.status)}>
                {program.status_display}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              {getStatusDescription(program.status)}
            </p>
          </div>

          {/* Status Dropdown */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">
                Change Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full px-4 py-3 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="draft">Draft - Not visible to vendors</option>
                <option value="review">Review - Under internal review</option>
                <option value="active">Active - Open for vendor submissions</option>
                <option value="archived">Archived - Closed to new submissions</option>
              </select>
            </div>

            {/* Status Change Warning */}
            {status !== program.status && status === 'active' && (
              <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                      Make Program Active?
                    </p>
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      Setting the program to Active will make it visible to vendors on the submissions page. 
                      They will be able to view program details and submit proposals.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {status !== program.status && status === 'archived' && (
              <div className="p-4 bg-warning/10 border border-warning/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-warning mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-900 dark:text-amber-100 mb-1">
                      Archive Program?
                    </p>
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                      Archiving the program will prevent new vendor submissions. Existing submissions will remain accessible.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Program Info Card (Read-only) */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">
            Additional Information
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-1">
                Program Manager
              </label>
              <p className="text-foreground">{program.primaryContact}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-card-foreground mb-1">
                Submissions
              </label>
              <p className="text-foreground">{program.submissions_count} total</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-card-foreground mb-1">
                Created
              </label>
              <p className="text-foreground">{program.submittedAt}</p>
            </div>
          </div>
        </Card>

        {/* Save Button */}
        <div className="mt-8 flex items-center justify-end gap-4">
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={saving || !hasChanges}
          >
            Reset
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProgramSettings;
